import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Lock, Monitor, Smartphone } from "lucide-react";
import { api } from "../../lib/api";
import type { IngestItemDetail } from "../../lib/ingestReviewApi";
import { BAND_LABEL, STATUS_LABEL, courseLibraryApi, courseLibraryItemApi, type LibItem, type LibKind } from "../../lib/courseLibraryApi";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { Block, Question } from "../../pages/admin/AdminIngestReviewPage";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { Skeleton } from "../ui/Skeleton";
import { toast } from "../ui/Toast";
import { mapApiError } from "../ui/StatusMessage";
import { cleanSource } from "../../lib/sourceRef";
import "../student/notes/chapter-notes.css";

type Props = {
  open: boolean;
  item: LibItem | null;
  /** Ids of the filtered list, in order, for Previous / Next. */
  ids: string[];
  token: string;
  courseId: string;
  kind: LibKind;
  chapters: { key: string; order: number | null; title: string }[];
  onNavigate: (id: string) => void;
  onClose: () => void;
  /** Reload the list after a change. */
  onChanged: () => void;
};

const field = "field w-full rounded-control px-3 py-2 text-base sm:text-sm";

/** Everything an admin does to one generated item, in place: read it as students do, edit, fix figures, decide. */
export function LibraryItemDrawer({ open, item, ids, token, courseId, kind, chapters, onNavigate, onClose, onChanged }: Props) {
  const [detail, setDetail] = useState<IngestItemDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [locked, setLocked] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [asking, setAsking] = useState<null | "reject" | "regenerate">(null);
  const [showDiscarded, setShowDiscarded] = useState(false);

  // edit form
  const [qText, setQText] = useState("");
  const [opts, setOpts] = useState<{ label: string; text: string }[]>([]);
  const [correct, setCorrect] = useState("");
  const [expl, setExpl] = useState("");
  const [diff, setDiff] = useState("");
  const [blocksJson, setBlocksJson] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [moving, setMoving] = useState(false);
  const [moveTo, setMoveTo] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const id = item?.id;
  const load = useCallback(
    async (discarded = false) => {
      if (!id) return;
      setLoading(true);
      try {
        const d = await api.admin.ingestReview.get(token, id, discarded);
        setDetail(d);
        const p = d.payload;
        setQText(String(p.question_text ?? ""));
        setOpts((p.options ?? []).map((o: any) => ({ label: String(o.label), text: String(o.text ?? "") })));
        setCorrect(String(p.correct_answer?.labels?.[0] ?? ""));
        setExpl(String(p.explanation ?? ""));
        setDiff(p.difficulty == null ? "" : String(p.difficulty));
        setBlocksJson(JSON.stringify(p.blocks ?? [], null, 2));
        setSourceText(String(p.source_ref?.label ?? ""));
      } catch (e) {
        toast.error(mapApiError(e).message);
      } finally {
        setLoading(false);
      }
    },
    [id, token],
  );

  useEffect(() => {
    setEditing(false);
    setAsking(null);
    setNote("");
    setShowDiscarded(false);
    setMoving(false);
    setDetail(null);
    if (open) void load();
  }, [open, id, load]);

  const pos = id ? ids.indexOf(id) : -1;
  const isQuestion = detail?.kind === "exam_question";
  const canEdit = detail && detail.status !== "published";
  const p = detail?.payload ?? {};

  async function patch(body: Parameters<typeof api.admin.ingestReview.patch>[2], ok: string) {
    if (!id) return;
    setBusy(true);
    try {
      const r = await api.admin.ingestReview.patch(token, id, body);
      if (r.blocked?.length) toast.info("This one needs a decision before it can be approved. Open Content review to choose.");
      else toast.success(ok);
      setAsking(null);
      setEditing(false);
      onChanged();
      await load();
    } catch (e) {
      toast.error(mapApiError(e).message, { action: { label: "Try again", onClick: () => void patch(body, ok) } });
    } finally {
      setBusy(false);
    }
  }

  async function saveEdits() {
    let edits: Record<string, unknown>;
    if (isQuestion) {
      edits = { question_text: qText, options: opts.map((o) => ({ ...(((p.options ?? []).find((x: any) => x.label === o.label)) ?? {}), label: o.label, text: o.text })), correct_answer: { ...(p.correct_answer ?? {}), labels: correct ? [correct] : [] }, explanation: expl };
      if (diff) edits.difficulty = isNaN(Number(diff)) ? diff : Number(diff);
    } else {
      try {
        edits = { blocks: JSON.parse(blocksJson) };
      } catch {
        toast.error("The notes are not valid JSON. Check the commas and brackets.");
        return;
      }
    }
    // The source line shows to students only when it has text, so an empty value removes it.
    edits.source_ref = sourceText.trim() ? { ...(p.source_ref ?? {}), label: sourceText.trim() } : null;
    await patch({ edits }, "Changes saved");
  }

  async function duplicate() {
    if (!id) return;
    setBusy(true);
    try {
      const copy = await courseLibraryItemApi.duplicate(token, courseId, id);
      toast.success("Copy created as a draft");
      onChanged();
      onNavigate(copy.id);
    } catch (e) {
      toast.error(mapApiError(e).message, { action: { label: "Try again", onClick: () => void duplicate() } });
    } finally {
      setBusy(false);
    }
  }

  async function moveChapter() {
    const ch = chapters.find((c) => c.key === moveTo);
    if (!id || !ch || ch.order == null) return;
    setBusy(true);
    try {
      await courseLibraryApi.bulk(token, courseId, { kind, action: "move_chapter", ids: [id], chapter_order: ch.order, chapter_title: ch.title });
      toast.success(`Moved to chapter ${ch.order}`);
      setMoving(false);
      onChanged();
      await load();
    } catch (e) {
      toast.error(mapApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!id) return;
    setBusy(true);
    try {
      await courseLibraryItemApi.remove(token, courseId, id);
      toast.success("Item deleted");
      setConfirmDelete(false);
      onChanged();
      const next = ids[ids.indexOf(id) + 1] ?? ids[ids.indexOf(id) - 1];
      if (next) onNavigate(next);
      else onClose();
    } catch (e) {
      toast.error(mapApiError(e).message);
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  async function figure(fid: string, action: "remove" | "restore") {
    if (!id) return;
    setBusy(true);
    try {
      setDetail(await api.admin.ingestReview.figure(token, id, fid, action));
      toast.success(action === "remove" ? "Image removed" : "Image restored");
      onChanged();
    } catch (e) {
      toast.error(mapApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  const figures = useMemo(() => (detail?.payload.figures ?? []).filter((f: any) => f?.figure_external_id), [detail]);
  const discarded = useMemo(() => (detail?.payload.figure_decisions ?? []).filter((d: any) => d.decision === "discarded"), [detail]);
  const data = detail?.figure_data ?? {};
  const src = cleanSource(p.source_ref?.label) ?? cleanSource(item?.has_source ? `${detail?.source?.file ?? ""}${detail?.source?.page_start ? ` p.${detail.source.page_start}` : ""}` : "");

  return (
    <Modal isOpen={open} onClose={onClose} size="xl" title={item ? `${item.kind === "note" ? item.topic || "Notes" : `Question ${item.number ?? ""}`}` : "Item"}>
      {!item ? null : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" disabled={pos <= 0} onClick={() => onNavigate(ids[pos - 1])} icon={<ChevronLeft size={14} />}>Previous</Button>
            <span className="text-sm text-muted-foreground">{pos + 1} of {ids.length}</span>
            <Button size="sm" variant="outline" disabled={pos < 0 || pos >= ids.length - 1} onClick={() => onNavigate(ids[pos + 1])} icon={<ChevronRight size={14} />} iconPosition="right">Next</Button>
            <span className="ml-auto flex flex-wrap items-center gap-1.5">
              <Badge variant={item.status === "published" ? "success" : item.status === "rejected" ? "destructive" : "warning"}>{STATUS_LABEL[item.status]}</Badge>
              <Badge variant={item.band === "high" ? "success" : item.band === "low" ? "destructive" : "secondary"}>
                {BAND_LABEL[item.band]}{item.confidence != null ? ` ${Math.round(item.confidence * 100)}%` : ""}
              </Badge>
            </span>
          </div>

          {(item.reasons.length > 0 || item.chapter_title) && (
            <div className="rounded-tile bg-soft p-3 text-sm">
              {item.chapter_title && <p className="text-muted-foreground">Chapter {item.chapter_order ?? "-"}: {item.chapter_title}{item.topic ? ` · ${item.topic}` : ""}{item.difficulty ? ` · Difficulty ${item.difficulty}` : ""}</p>}
              {item.reasons.length > 0 && (
                <p className="mt-1 flex flex-wrap items-center gap-1.5">
                  <span className="font-semibold text-foreground">Audit:</span>
                  {item.reasons.map((r) => <Badge key={r} variant="warning">{r.replace(/_/g, " ")}</Badge>)}
                </p>
              )}
              {src && <p className="mt-1 text-muted-foreground">Source: {src}</p>}
            </div>
          )}

          {loading || !detail ? (
            <Skeleton className="h-56 w-full rounded-card" />
          ) : editing ? (
            <div className="space-y-3">
              {isQuestion ? (
                <>
                  <label className="block text-sm font-semibold">Question<textarea className={`${field} mt-1 min-h-24`} value={qText} onChange={(e) => setQText(e.target.value)} /></label>
                  {opts.map((o, i) => (
                    <label key={o.label} className="block text-sm font-semibold">Option {o.label}
                      <input className={`${field} mt-1`} value={o.text} onChange={(e) => setOpts((prev) => prev.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
                    </label>
                  ))}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block text-sm font-semibold">Correct answer
                      <select className={`${field} mt-1`} value={correct} onChange={(e) => setCorrect(e.target.value)}>
                        <option value="">Choose</option>
                        {opts.map((o) => <option key={o.label} value={o.label}>{o.label}</option>)}
                      </select>
                    </label>
                    <label className="block text-sm font-semibold">Difficulty<input className={`${field} mt-1`} value={diff} onChange={(e) => setDiff(e.target.value)} /></label>
                  </div>
                  <label className="block text-sm font-semibold">Explanation<textarea className={`${field} mt-1 min-h-20`} value={expl} onChange={(e) => setExpl(e.target.value)} /></label>
                </>
              ) : (
                <label className="block text-sm font-semibold">Notes blocks (JSON)
                  <textarea className={`${field} mt-1 min-h-64 font-mono`} value={blocksJson} onChange={(e) => setBlocksJson(e.target.value)} />
                </label>
              )}
              <label className="block text-sm font-semibold">Source (optional)
                <input className={`${field} mt-1`} value={sourceText} onChange={(e) => setSourceText(e.target.value)} placeholder="e.g. Physics module, p. 42. Leave empty for none." />
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                <Button loading={busy} loadingText="Saving…" onClick={() => void saveEdits()}>Save changes</Button>
              </div>
            </div>
          ) : (
            <>
              {/* Preview exactly as a student sees it, with a phone / desktop switch and a premium lock preview */}
              <div className="flex flex-wrap items-center gap-2">
                <div role="group" aria-label="Preview size" className="inline-flex rounded-btn border border-border p-0.5">
                  <button type="button" aria-pressed={device === "desktop"} onClick={() => setDevice("desktop")} className={`inline-flex min-h-9 items-center gap-1 rounded-btn px-3 text-sm ${device === "desktop" ? "bg-accent-soft text-primary" : "text-muted-foreground"}`}><Monitor size={14} /> Desktop</button>
                  <button type="button" aria-pressed={device === "mobile"} onClick={() => setDevice("mobile")} className={`inline-flex min-h-9 items-center gap-1 rounded-btn px-3 text-sm ${device === "mobile" ? "bg-accent-soft text-primary" : "text-muted-foreground"}`}><Smartphone size={14} /> Phone</button>
                </div>
                <label className="inline-flex min-h-9 items-center gap-2 text-sm"><input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)} className="h-4 w-4" /> Show as locked (Premium)</label>
              </div>
              <div className={`mx-auto w-full ${device === "mobile" ? "max-w-[375px]" : "max-w-3xl"}`}>
                <div className="chapter-notes relative" data-paper="dark" style={{ ["--cn-min-size" as string]: "16px" }}>
                  <div className={`cn-slide cn-body space-y-3 ${locked ? "pointer-events-none select-none blur-sm" : ""}`}>
                    {isQuestion ? <Question p={p} data={data} onRemoveFigure={canEdit ? (fid) => void figure(fid, "remove") : undefined} /> : (p.blocks ?? []).map((b: any, i: number) => <Block key={i} b={b} data={data} onRemoveFigure={canEdit ? (fid) => void figure(fid, "remove") : undefined} />)}
                  </div>
                  {locked && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className="inline-flex items-center gap-2 rounded-tile bg-card px-4 py-3 text-sm font-semibold text-foreground shadow-lg"><Lock size={16} /> Premium: students without access see this lock</p>
                    </div>
                  )}
                </div>
              </div>

              {canEdit && discarded.length > 0 && (
                <div className="space-y-2">
                  <Button size="sm" variant="ghost" onClick={() => { setShowDiscarded(true); void load(true); }}>Discarded images ({discarded.length})</Button>
                  {showDiscarded && discarded.map((d: any) => (
                    <div key={d.figure_external_id} className="flex items-center gap-3 rounded-tile bg-soft p-2 text-sm">
                      {data[d.figure_external_id] && <img src={data[d.figure_external_id]} alt={d.caption || "Discarded image"} className="h-16 rounded-md" />}
                      <span className="min-w-0 flex-1 truncate text-muted-foreground">Page {d.page} · {d.reason}</span>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => void figure(d.figure_external_id, "restore")}>Restore</Button>
                    </div>
                  ))}
                </div>
              )}
              {figures.length === 0 && item.kind !== "note" && <p className="text-xs text-muted-foreground">No figure on this item. Images come from the module page the item was read from.</p>}

              {asking && (
                <div className="space-y-2 rounded-tile border border-border p-3">
                  <label className="block text-sm font-semibold">{asking === "reject" ? "What is wrong? The next run reads this." : "What should change when it is regenerated?"}
                    <textarea className={`${field} mt-1 min-h-20`} value={note} onChange={(e) => setNote(e.target.value)} />
                  </label>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setAsking(null)}>Cancel</Button>
                    <Button variant="destructive" loading={busy} disabled={asking === "reject" && !note.trim()} onClick={() => void patch({ status: "rejected", note: asking === "regenerate" ? `Regenerate: ${note.trim() || "please regenerate this item"}` : note.trim() }, asking === "regenerate" ? "Sent back for regeneration" : "Rejected")}>
                      {asking === "regenerate" ? "Send for regeneration" : "Reject with note"}
                    </Button>
                  </div>
                </div>
              )}

              {moving && (
                <div className="flex flex-wrap items-end gap-2 rounded-tile border border-border p-3">
                  <label className="min-w-0 flex-1 text-sm font-semibold">Move to chapter
                    <select className={`${field} mt-1`} value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                      <option value="">Choose a chapter</option>
                      {chapters.filter((c) => c.order != null).map((c) => <option key={c.key} value={c.key}>{c.order}. {c.title}</option>)}
                    </select>
                  </label>
                  <Button variant="ghost" onClick={() => setMoving(false)}>Cancel</Button>
                  <Button disabled={!moveTo} loading={busy} onClick={() => void moveChapter()}>Move</Button>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
                <Button variant="ghost" onClick={() => void duplicate()} disabled={busy}>Duplicate</Button>
                {canEdit && <Button variant="ghost" onClick={() => setMoving((v) => !v)}>Move</Button>}
                {canEdit && <Button variant="ghost" onClick={() => setConfirmDelete(true)}>Delete</Button>}
                {canEdit && <Button variant="outline" onClick={() => setEditing(true)}>Edit</Button>}
                {detail.status !== "published" && <Button variant="outline" onClick={() => setAsking("regenerate")}>Regenerate</Button>}
                {detail.status !== "rejected" && detail.status !== "published" && <Button variant="outline" onClick={() => setAsking("reject")}>Reject</Button>}
                {detail.status === "published" && <Button variant="outline" loading={busy} onClick={() => void patch({ status: "draft" }, "Sent back to draft")}>Back to draft</Button>}
                {detail.status !== "published" && <Button loading={busy} onClick={() => void patch({ status: "published" }, "Approved")}>Approve</Button>}
              </div>
            </>
          )}
        </div>
      )}
      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete this item?"
        message="It is removed for good. Approved items cannot be deleted; send them back to draft first."
        variant="destructive"
        confirmText="Delete"
        loading={busy}
        onConfirm={remove}
        onCancel={() => setConfirmDelete(false)}
      />
    </Modal>
  );
}
